import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import useApi from '../../../../hook/useApi';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import cn from '../../../../utils/cn';
import { GetCurrencySymbol } from '../../../../utils/Currency';
import {
    addOption,
    InputCheckbox,
    InputDateField,
    SelectOption,
    TextareaField
} from '../../../../utils/InputField';
import { GetFloatNumber } from '../../../../utils/NumberFormatter';
import { ImageUploadingIcon } from '../../../../utils/SVGIcon';
import { mkRelativeUrl } from '../../../../utils/URL';
import { calcVat } from '../../../../utils/VatCalculator';
import { ApiGetPurchaseServicesList } from '../../../Settings/Services/ServiceOperation';

const billingCycles = [
    { key: 1, value: 'monthly', text: 'Monthly' },
    { key: 3, value: 'quarterly', text: 'Quarterly' },
    { key: 6, value: 'half-yearly', text: 'Half-Yearly' },
    { key: 12, value: 'yearly', text: 'Yearly' }
];

function PackageItems({ arrayList, handlePackageSelect, handleCustomInput, checkedItems }) {
    if (arrayList?.length === 0) {
        return (
            <div className="h-full flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                <span className="text-gray-500">
                    No Package found for this service or user current currency!
                </span>
            </div>
        );
    }
    return (
        <ul className="w-full bg-secondary-50 rounded-10 divide-y">
            {arrayList?.map((option) => {
                return (
                    <li key={option.id} className="w-full flex flex-col p-3">
                        <span className="text-[18px] font-medium line-clamp-1">
                            {option?.serviceTitle}
                        </span>
                        <div className="ml-3 flex flex-col gap-2">
                            {option?.packages?.map((el) => {
                                return (
                                    <label
                                        htmlFor={el?.pricingId}
                                        key={el?.pricingId}
                                        className="size-full flex items-start justify-start gap-3 px-3 py-1"
                                    >
                                        <input
                                            type="checkbox"
                                            id={el?.pricingId}
                                            className="mt-1"
                                            onChange={(e) => handlePackageSelect(e, el, option)}
                                        />
                                        <div className="flex flex-wrap justify-between items-baseline w-full text-base font-normal gap-x-3 gap-y-1">
                                            <p className="flex gap-1 items-baseline">
                                                <span>{el?.packageName}</span>
                                                <span className="text-sm font-normal whitespace-nowrap">
                                                    ({el?.unitName})
                                                </span>
                                            </p>
                                            {el?.amount === -101 ? (
                                                <span className="whitespace-nowrap">
                                                    {GetCurrencySymbol(el?.currency)}{' '}
                                                    <input
                                                        type="text"
                                                        onChange={(e) => handleCustomInput(e, el)}
                                                        ref={(input) => {
                                                            if (input) {
                                                                if (
                                                                    !checkedItems.includes(
                                                                        el.packageId
                                                                    )
                                                                ) {
                                                                    // eslint-disable-next-line no-param-reassign
                                                                    input.value = '';
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Enter Amount"
                                                        className="w-30 h-8 p-1 rounded-5 border border-gray-300 focus:outline-none disabled:bg-primary-100"
                                                        disabled={
                                                            !checkedItems.includes(el.packageId)
                                                        }
                                                    />
                                                </span>
                                            ) : (
                                                <span className="whitespace-nowrap">
                                                    {el?.amount === -102 ? (
                                                        <span>Free</span>
                                                    ) : (
                                                        <span>
                                                            {GetCurrencySymbol(el?.currency)}
                                                            {GetFloatNumber(el?.amount)}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function AddNewServiceModal({ closeModal, data }) {
    const { setMessage } = useTemplate();
    const { id, currency } = data;
    const [checkedItems, setCheckedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [servicesList, setServicesList] = useState([]);
    const [formData, setFormData] = useState([]);
    const navigate = useNavigate();
    const { post } = useApi();

    const [formData2, setFormData2] = useState({
        ServiceStartDate: dayjs().format('YYYY-MM-DD'),
        IsQuotation: false,
        BillingCycle: 'yearly',
        Comment: '',
        VatEnabled: false,
        VatType: 'excluding',
        VatPercent: process.env.REACT_APP_VAT_PERCENT || '7'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name) {
            setFormData2((prev) => ({ ...prev, [name]: value }));
        }
    };

    useEffect(() => {
        setIsLoading(true);
        ApiGetPurchaseServicesList(id)
            .then(({ data: resData }) => {
                setServicesList(resData.services);
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                setMessage({ type: 'error', text: err.toString() });
            });
    }, [id, setMessage]);

    const handlePackageSelect = (e, packageItem, serviceItem) => {
        setCheckedItems((prev) => {
            if (prev.includes(packageItem.packageId)) {
                return prev.filter((el) => el !== packageItem.packageId);
            }
            return [...prev, packageItem.packageId];
        });
        const { checked } = e.target;
        if (checked) {
            if (packageItem?.amount === -101) {
                if (formData.some((el) => el.AppServicePackageId === packageItem.packageId)) {
                    return null;
                }
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            } else if (packageItem?.amount === -102) {
                if (formData.some((el) => el.AppServicePackageId === packageItem.packageId)) {
                    return null;
                }
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            } else {
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: packageItem?.amount || 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            }
        }
        if (!checked) {
            setFormData((prev) =>
                prev.filter((el) => el.AppServicePackageId !== packageItem.packageId)
            );
        }
        return null;
    };

    const payload = useMemo(() => {
        const vatPercent = formData2.VatEnabled ? parseFloat(formData2.VatPercent) || 0 : 0;
        const vatType = formData2.VatEnabled ? formData2.VatType : 'excluding';
        return {
            CustomerId: id,
            PackageData: formData.map((item) => ({
                ...item,
                VatPercent: vatPercent,
                VatType: vatType
            })),
            Comment: formData2.Comment || '',
            BillingCycle: String(formData2.BillingCycle) || 'yearly',
            ServiceStartDate: formData2.ServiceStartDate,
            IsQuotation: formData2.IsQuotation || false,
            DefaultVatEnabled: formData2.VatEnabled,
            DefaultVatType: vatType,
            DefaultVatPercent: vatPercent
        };
    }, [formData, id, formData2]);

    const handleCustomInput = (e, packageItem) => {
        const { value } = e.target;
        const index = formData.findIndex((el) => el.AppServicePackageId === packageItem.packageId);
        if (index !== -1) {
            setFormData((prev) => {
                // eslint-disable-next-line no-param-reassign
                prev[index].Amount = Number(value) || 0;
                return [...prev];
            });
        } else {
            setFormData((prev) => [
                ...prev,
                {
                    AppServiceId: packageItem.AppServiceId,
                    AppServicePackageId: packageItem.packageId,
                    Amount: Number(value) || 0,
                    IsDurationBased: packageItem.isDurationBased
                }
            ]);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setBtnLoading(true);
        post(`/api/purchase/service/create-invoice`, payload)
            .then(({ data: resData, error, message }) => {
                if (error) {
                    throw new Error(message);
                }
                setMessage({ type: 'success', text: message });
                navigate(mkRelativeUrl(`/billing/invoices/${resData.invoiceId}/info`));
                closeModal();
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error?.message });
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };

    // Pre-compute VAT totals for the summary table
    const baseTotal = formData.reduce((acc, curr) => {
        let amount = curr.Amount;
        if (curr.IsDurationBased) {
            if (formData2.BillingCycle === 'half-yearly') amount = curr.Amount / 2;
            else if (formData2.BillingCycle === 'quarterly') amount = curr.Amount / 4;
            else if (formData2.BillingCycle === 'monthly') amount = curr.Amount / 12;
        }
        return acc + amount;
    }, 0);
    const vatTotals = calcVat(
        baseTotal,
        formData2.VatEnabled ? formData2.VatPercent : 0,
        formData2.VatType
    );

    const isBillingCycleDisabled = formData.every((item) => {
        const service = servicesList.find((sr) =>
            sr.packages.some(
                (pkg) => pkg.packageId === item.AppServicePackageId && !sr.isDurationBased
            )
        );
        return service !== undefined;
    });

    return (
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-3 min-h-full', {})}>
            <div className="flex flex-col xl:flex-row gap-3 xl:gap-4 xl:flex-1 xl:min-h-0 xl:max-h-full">
                <div className="flex flex-col gap-2 w-full xl:w-[35%] xl:overflow-y-auto xl:flex-1 xl:min-h-0 pr-2">
                    <div>
                        <InputDateField
                            label="Service Start Date"
                            name="ServiceStartDate"
                            formData={formData2}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <SelectOption
                        label="Billing Cycle"
                        name="BillingCycle"
                        formData={formData2}
                        onChange={handleChange}
                        options={billingCycles.map((val) =>
                            addOption(val.key, val.text, val.value)
                        )}
                        required
                        disabled={isBillingCycleDisabled}
                    />
                    <div className="text-sm text-error mt-1">
                        * The billing cycle is applicable only to yearly packages.
                    </div>

                    <TextareaField
                        formData={formData2}
                        name="Comment"
                        label="Reference"
                        placeholder="Type your comment here..."
                        onChange={handleChange}
                        optional
                    />

                    <InputCheckbox
                        label="Bill To Agency"
                        name="IsQuotation"
                        formData={formData2}
                        onChange={(e) =>
                            setFormData2((prev) => ({ ...prev, IsQuotation: e.target.checked }))
                        }
                        text="Create as Quotation"
                        className="mt-2 "
                    />
                    <div className="text-sm text-error mt-1">
                        * If you check &quot;Create as Quotation&quot;, the invoice will be created
                        as a quotation and you can convert it to an invoice after approval.
                    </div>

                    {/* VAT Configuration */}
                    <div className="flex flex-col gap-1 border-t pt-2 mt-1">
                        <span className="text-sm font-medium">VAT Configuration</span>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={formData2.VatEnabled}
                                onChange={(e) =>
                                    setFormData2((prev) => ({
                                        ...prev,
                                        VatEnabled: e.target.checked
                                    }))
                                }
                            />
                            <span>Enable VAT</span>
                        </label>
                        {formData2.VatEnabled && (
                            <div className="flex flex-col gap-2 ml-1 pl-3 border-l-2 border-base-300">
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-gray-600">
                                        VAT Type
                                    </span>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="VatType"
                                            value="excluding"
                                            checked={formData2.VatType === 'excluding'}
                                            onChange={handleChange}
                                            className="radio radio-xs"
                                        />
                                        <span>Excluding VAT</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="VatType"
                                            value="including"
                                            checked={formData2.VatType === 'including'}
                                            onChange={handleChange}
                                            className="radio radio-xs"
                                        />
                                        <span>Including VAT</span>
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">
                                        VAT Percent
                                    </label>
                                    <input
                                        type="number"
                                        name="VatPercent"
                                        value={formData2.VatPercent}
                                        onChange={handleChange}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        className="w-20 h-6 px-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:border-primary-500"
                                    />
                                    <span className="text-xs text-gray-500">%</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="w-full xl:w-[65%] flex flex-col xl:min-h-0">
                    <div className="min-h-[120px] md:min-h-[150px] xl:max-h-[400px] overflow-y-auto xl:flex-1 xl:overflow-y-auto border border-gray-300 rounded-10 bg-secondary-50 p-2 custom-scrollbar">
                        {isLoading && (
                            <div className="flex h-40 justify-center items-center">
                                <ImageUploadingIcon />
                            </div>
                        )}
                        {!isLoading && servicesList?.length === 0 ? (
                            <div className="h-40 flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                                <span className="text-gray-500">
                                    No service found or user current currency!
                                </span>
                            </div>
                        ) : (
                            <PackageItems
                                arrayList={servicesList}
                                handlePackageSelect={handlePackageSelect}
                                handleCustomInput={handleCustomInput}
                                checkedItems={checkedItems}
                            />
                        )}
                    </div>
                    <div className="border-t pt-1 mt-1 bg-white overflow-y-auto max-h-[120px] md:max-h-[150px] xl:max-h-none">
                        <table className="w-full text-xs">
                            <tbody className="divide-y">
                                <tr className="flex justify-between items-center py-1">
                                    <td className="text-xs font-medium">Recurring Amount</td>
                                    <td className="text-right whitespace-nowrap ml-1 text-xs">
                                        {GetCurrencySymbol(currency)}
                                        {GetFloatNumber(
                                            formData.reduce((acc, curr) => {
                                                if (curr.IsDurationBased) {
                                                    if (formData2.BillingCycle === 'yearly') {
                                                        return acc + curr.Amount;
                                                    }
                                                    if (formData2.BillingCycle === 'half-yearly') {
                                                        return acc + curr.Amount / 2;
                                                    }
                                                    if (formData2.BillingCycle === 'quarterly') {
                                                        return acc + curr.Amount / 4;
                                                    }
                                                    if (formData2.BillingCycle === 'monthly') {
                                                        return acc + curr.Amount / 12;
                                                    }
                                                    return acc;
                                                }
                                                return acc;
                                            }, 0)
                                        )}
                                    </td>
                                </tr>
                                <tr className="flex justify-between items-center py-1">
                                    <td className="text-xs font-medium">One-Time Amount</td>
                                    <td className="text-right whitespace-nowrap ml-1 text-xs">
                                        {GetCurrencySymbol(currency)}
                                        {GetFloatNumber(
                                            formData.reduce(
                                                (acc, curr) =>
                                                    curr.IsDurationBased ? acc : acc + curr.Amount,
                                                0
                                            )
                                        )}
                                    </td>
                                </tr>
                                {formData2.VatEnabled ? (
                                    <>
                                        <tr className="flex justify-between items-center py-1">
                                            <td className="text-xs font-medium">Sub Total</td>
                                            <td className="text-right whitespace-nowrap ml-1 text-xs">
                                                {GetCurrencySymbol(currency)}
                                                {GetFloatNumber(vatTotals.netAmount)}
                                            </td>
                                        </tr>
                                        <tr className="flex justify-between items-center py-1">
                                            <td className="text-xs font-medium">
                                                VAT ({formData2.VatPercent}%)
                                            </td>
                                            <td className="text-right whitespace-nowrap ml-1 text-xs">
                                                {GetCurrencySymbol(currency)}
                                                {GetFloatNumber(vatTotals.vatAmount)}
                                            </td>
                                        </tr>
                                        <tr className="flex justify-between items-center py-1 font-bold border-t-2 border-gray-300">
                                            <td className="text-xs">Grand Total</td>
                                            <td className="text-right whitespace-nowrap ml-1 text-xs">
                                                {GetCurrencySymbol(currency)}
                                                {GetFloatNumber(vatTotals.grossAmount)}
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr className="flex justify-between items-center py-1 font-bold border-t-2 border-gray-300">
                                        <td className="text-xs">Total</td>
                                        <td className="text-right whitespace-nowrap ml-1 text-xs">
                                            {GetCurrencySymbol(currency)}
                                            {GetFloatNumber(baseTotal)}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="w-full flex justify-end items-center gap-2 pt-2 border-t mt-auto">
                <ButtonCancel
                    title="Cancel"
                    onClick={() => closeModal(false)}
                    className="flex-shrink-0"
                />
                <ButtonPrimary
                    title="Create Invoice"
                    btnType="submit"
                    loading={btnLoading}
                    className="flex-shrink-0"
                />
            </div>
        </form>
    );
}

export default AddNewServiceModal;
